import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import {
  Dialog, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import { IPortal, useStore } from '@mcn-platform/models';
import {
  DUAL_SEARCH_SYSTEM_PROMPT,
  AI_MODE_SEARCH_PROMPT,
  SHOPPING_ASSISTANT_PROMPT,
  ORDER_STATUS_TOOL_PROMPT,
  EXIT_TOOL_PROMPT,
  PRODUCT_CONTENT_PROMPT,
  CATEGORY_CONTENT_PROMPT,
  ASK_ABOUT_PRODUCT_WIDGET_PROMPT,
} from '../constants/systemPromptDefaults';

/** Match backend buildSystemPrompt fallback: shoppingAssistantPrompt → clientAIPromptTemplate → default */
const getPromptFromKey = (portal: IPortal, key: string) => {
  const portalWithTemplate = portal as IPortal & { clientAIPromptTemplate?: string | null };
  switch (key) {
    case 'shoppingAssistant':
      return portal?.clientAIConfig?.shoppingAssistantPrompt
        || portalWithTemplate?.clientAIPromptTemplate
        || SHOPPING_ASSISTANT_PROMPT;
    case 'aiSearch':
      // Portal setting then system fallback only; never clientAIPromptTemplate
      return portal?.clientAIConfig?.aiModeSearchPrompt || AI_MODE_SEARCH_PROMPT;
    case 'dualSearch':
      // Portal setting then system fallback only; never clientAIPromptTemplate
      return portal?.clientAIConfig?.dualSearchPrompt || DUAL_SEARCH_SYSTEM_PROMPT;
    case 'orderStatus':
      return portal?.clientAIConfig?.orderStatusToolPrompt || ORDER_STATUS_TOOL_PROMPT;
    case 'exitTool':
      return portal?.clientAIConfig?.exitToolPrompt || EXIT_TOOL_PROMPT;
    case 'productContent':
      return portal?.clientAIConfig?.productContentPrompt || PRODUCT_CONTENT_PROMPT;
    case 'categoryContent':
      return portal?.clientAIConfig?.categoryContentPrompt || CATEGORY_CONTENT_PROMPT;
    case 'askAboutProductWidget':
      return portal?.clientAIConfig?.askAboutProductWidgetPrompt || ASK_ABOUT_PRODUCT_WIDGET_PROMPT;
    default:
      return '';
  }
};

type BasePromptModalContextValue = {
  openModal: (promptKey: string) => void;
};

const BasePromptModalContext = createContext<BasePromptModalContextValue | null>(null);

export function useBasePromptModal(): BasePromptModalContextValue {
  const ctx = useContext(BasePromptModalContext);
  if (!ctx) throw new Error('useBasePromptModal must be used within BasePromptModalProvider');
  return ctx;
}

type BasePromptModalProviderProps = {
  children: React.ReactNode;
};

/**
 * Singleton modal for viewing base (override or default) per append key.
 * Append key → base content via getBaseForAppendField(portal, promptKey).
 */
export const BasePromptModalProvider = ({ children }: BasePromptModalProviderProps) => {
  const store = useStore();
  const portal = store.currentPortal;
  const [promptKey, setpromptKey] = useState<string | null>(null);

  const openModal = useCallback((key: string) => {
    setpromptKey(key);
  }, []);

  const closeModal = useCallback(() => {
    setpromptKey(null);
  }, []);

  const value = useMemo(() => ({ openModal }), [openModal]);

  const baseContent = promptKey && portal
    ? getPromptFromKey(portal, promptKey)
    : '';

  return (
    <BasePromptModalContext.Provider value={value}>
      {children}
      <Dialog
        open={promptKey !== null}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '40vh' } }}
      >
        <DialogTitle>Current system prompt</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            value={baseContent}
            variant="outlined"
            inputProps={{ readOnly: true }}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
        </DialogContent>
      </Dialog>
    </BasePromptModalContext.Provider>
  );
};
